import { computed, ref, watch } from 'vue'
import { useUrlParamStore } from '~/routing/urlParam'
import { defineStore, getActivePinia } from 'pinia'
import { useSessionStorage } from '@vueuse/core'
import axios from 'axios'
import type { components } from '~/generated/types'
import { allPages } from '~/data/pagination'
import { useDataStore } from '~/data'
import { compact } from 'lodash-es'
import { useStore } from '~/store'
import type { BackendError } from '~/types'
import { useRouter } from 'vue-router'

import Error from './Error.vue'

export type ShareLink = components['schemas']['ShareLinkCreate']
export type ShareableObject = Pick<ShareLink, 'object_type' | 'object_id'>
export { default as ShareLinkModal } from './ShareLinkModal.vue'

// ShareLink feature
// Sharer:
// - Multiple tokens can be generated per object
// - Each token can be separately revoked - or decays with explicit automatic expiry
// - Sharer can manage sharelinks in two places: Modal (per-object) and Manage (all)
// Guest:
// - Received sharelink Tokens are stored in ephemeral keychain (deleted on reload)
// - New received token per object overrides old one
// - Pages are responsible for associating objects that contain protected uploads, e.g. all tracks per shared album
// - Guest can navigate and continue listening, but tokens are lost on logout
// - After page reload without sharelink, queue is not sanitized -> previously shared tracks will linger and produce 404s

/**
 *
 * @returns sharelink token extracted from URL query parameter
 * - persisted in local storage (keyed by route)
 * - reactive (updates when URL query parameter changes)
 */
export function useShareLinkToken() {
  return useUrlParamStore('share_link_token', {
    scope: 'route',
    allowedValues: 'singleton'
  })
}

// Sharer: Get my own ShareLinks

export const useShareLinks = () => {
  const resource = allPages(useDataStore().get_sharelinks, {})

  // TODO(#2765, #2771): replace with mutation-triggered invalidation.
  const refetch = () => resource.value.refetch()

  return {
    sharelinks: computed(() => compact(resource.value.data ?? []).sort((a, b) => a.id - b.id)),
    isLoading: computed(() => resource.value.status === 'loading' || resource.value.status === 'queued'),
    error: computed(() => resource.value.error?.message),
    refetch
  }
}

// Sharer Ui: Error toaster

const reportError = (store: ReturnType<typeof useStore>, error: unknown) => {
  const backendErrors = (error as BackendError).backendErrors
  store.commit('ui/addMessage', {
    content: backendErrors?.length ? backendErrors.join('\n') : (error as Error).message,
    date: new Date()
  })
}

// Sharer: Mutate my own ShareLinks

export const useShareLinkMutations = () => {
  const store = useStore()
  const { refetch } = useShareLinks()
  const isSubmitting = ref(false)
  const updatingIds = ref(new Set<number>())
  const revokingIds = ref(new Set<number>())

  /**
   * @param object
   * @param expires_at
   * @returns whether link was created so that caller can clear draft
   */
  const create = async (object: ShareableObject, expires_at: string | null) => {
    isSubmitting.value = true
    try {
      await axios.post('sharelink/', { ...object, expires_at })
      await refetch()
      return true
    } catch (error) {
      reportError(store, error)
      return false
    } finally {
      isSubmitting.value = false
    }
  }

  const updateExpiry = async (link: ShareLink, expires_at: string | null) => {
    updatingIds.value.add(link.id)
    try {
      await axios.patch(`sharelink/${link.id}/`, { expires_at })
      await refetch()
    } catch (error) {
      reportError(store, error)
    } finally {
      updatingIds.value.delete(link.id)
    }
  }

  const revoke = async (link: ShareLink) => {
    revokingIds.value.add(link.id)
    try {
      await axios.delete(`sharelink/${link.id}/`)
      await refetch()
    } catch (error) {
      reportError(store, error)
    } finally {
      revokingIds.value.delete(link.id)
    }
  }

  return { isSubmitting, updatingIds, revokingIds, create, updateExpiry, revoke }
}

// Sharer: Object URLs

/**
 * @param object
 * @returns route location of an object's library detail page
 */
export const objectRoute = (object: ShareableObject) => ({
  name: `library.${object.object_type}s.detail`,
  params: { id: object.object_id }
})

/** Absolute URL of object's library page, plus share token appended (curried) */
export const useObjectUrl = () => {
  const router = useRouter()
  return (object: ShareableObject, share_link_token?: string) =>
    window.location.origin + router.resolve({
      ...objectRoute(object),
      query: share_link_token ? { share_link_token } : undefined
    }).href
}

//  Guest: Persist tokens in a keychain

export const useShareKeychain = defineStore('share-keychain', () => {
  const tokens = useSessionStorage<Record<string, string>>('fw-share-tokens', {})

  const register = (type: 'artist' | 'album' | 'track', id: string | number, token: string) => {
    tokens.value[`${type}:${id}`] = token
  }

  const get = (type: 'artist' | 'album' | 'track', id: string | number) =>
    tokens.value[`${type}:${id}`]

  /**
   * @param url
   * @param params
   * @returns First matching token, or undefined
   */
  const findMatchingToken = (url = '', params: Record<string, unknown> = {}) =>
    Object.entries(tokens.value).find(([key]) => {
      const [type, id] = key.split(':')
      return (
        String(params[type!]) === id
        || new RegExp(`(?:^|/)${type}s/${id}(?:/|$)`).test(url)
        || new RegExp(`[?&]${type}=${id}(?:&|$)`).test(url)
      )
    })?.[1]

  return { tokens, register, get, findMatchingToken }
})

// Guest: Install middleware and inject authorization header

let isInstalled = false

/**
 * Plugin for transport method (axios); injects known `Link-Share-Token` headers
 * into requests whose URL/params match a registered token
 */
export const installShareLinkInterceptor = () => {
  if (isInstalled) return
  isInstalled = true

  axios.interceptors.request.use((config) => {
    const token = useShareKeychain().findMatchingToken(
      config.url ?? '',
      config.params as Record<string, unknown> | undefined
    )
    if (token && !config.headers?.has('Link-Share-Token')) {
      config.headers?.set('Link-Share-Token', token)
    }
    return config
  })
}

// Guest: Register received token

/**
 * @param getObjects - lazy list of objects to associate with any received token in current URL
 *
 * Important:
 * - Call on each page that can possibly receive a share token via link
 * - Register token before attempting to fetch the object
 *
 * Note:
 * - Register child objects such as tracks as they arrive
 * - Debug using DevTools -> pinia -> sharelink
 */
export const useTokenFromUrl = (getObjects: () => ShareableObject[]) => {
  const token = useShareLinkToken()
  const keychain = useShareKeychain()

  watch(
    () => [token.value, getObjects()] as const,
    ([token, objects]) => {
      if (!token) return
      objects.forEach(({ object_type, object_id }) => keychain.register(object_type, object_id, token))
    },
    { immediate: true }
  )

  return Error
}

// Guest: Resolve token for track

/**
 * <audio>/<img> cannot carry headers, so share tokens for media URLs must be
 * resolved at play time and appended as a query param. Walks the containment
 * chain: track -> album -> artist.
 * @param track
 * @returns The share token for the track, or undefined if no token is available
 * TODO: As artist_credits can be multiple artists per track - only first one is matched,
 * else 403 - add `artistCredit.some(({ artist: { id }) => ...)`
 */
export const shareTokenForTrack = (track: {
  id: number
  albumId?: number
  artistId?: number
}) => {
  if (!getActivePinia()) return undefined // Pinia not available in test environment -> degrade gracefully
  const keychain = useShareKeychain()
  return (
    keychain.get('track', track.id)
    ?? (track.albumId && track.albumId > 0 ? keychain.get('album', track.albumId) : undefined)
    ?? (track.artistId && track.artistId > 0 ? keychain.get('artist', track.artistId) : undefined)
  )
}
